import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import AdminHeader from './components/AdminHeader';

const FinancialDocumentForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const [formData, setFormData] = useState({
    title: ''
  });
  
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      const loadDocument = async () => {
        try {
          // Get the proper API URL
          const getApiUrl = (endpoint) => {
            const baseUrl = process.env.NODE_ENV === 'production' ? 'https://fciveria.ge' : 'http://localhost:5000';
            return `${baseUrl}${endpoint}`;
          };

          // Get token from localStorage
          const storedUser = localStorage.getItem('fciveria_admin_user');
          const token = storedUser ? JSON.parse(storedUser).token : null;

          const response = await fetch(getApiUrl(`/api/financial-documents/${id}`), {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const document = await response.json();
            if (document) {
              setFormData({
                title: document.title
              });
              setFilePreview(document.file_name);
            }
          }
        } catch (error) {
          console.error('Error loading document:', error);
        }
      };
      loadDocument();
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        alert('გთხოვთ აირჩიოთ PDF ფაილი');
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) { // 10MB
        alert('ფაილი ძალიან დიდია. მაქსიმალური ზომა: 10MB');
        return;
      }
      setFile(selectedFile);
      setFilePreview(selectedFile.name);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isEdit && !file) {
      alert('გთხოვთ აირჩიოთ PDF ფაილი');
      return;
    }

    setSaving(true);
    setUploading(true);

    try {
      // Get the proper API URL
      const getApiUrl = (endpoint) => {
        const baseUrl = process.env.NODE_ENV === 'production' ? 'https://fciveria.ge' : 'http://localhost:5000';
        return `${baseUrl}${endpoint}`;
      };

      // Get token from localStorage
      const storedUser = localStorage.getItem('fciveria_admin_user');
      const token = storedUser ? JSON.parse(storedUser).token : null;

      // Create form data for direct upload
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      if (file) {
        formDataToSend.append('file', file);
      }

      const response = await fetch(getApiUrl('/api/financial-documents'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save document');
      }

      navigate('/_panel-7c39f8/financial-documents');
    } catch (error) {
      console.error('Error saving document:', error);
      alert(`შეცდომა: ${error.message}`);
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/_panel-7c39f8/financial-documents')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            უკან
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEdit ? 'ფინანსური დოკუმენტის რედაქტირება' : 'ახალი ფინანსური დოკუმენტი'}
          </h1>
        </div>

        <div className="bg-white shadow rounded-lg">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                სათაური *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="მაგ: 2024 წლის მე-3 კვარტალის ანგარიშგება"
              />
            </div>


            {/* File Upload */}
            <div>
              <label htmlFor="file" className="block text-sm font-medium text-gray-700 mb-2">
                PDF ფაილი {!isEdit && '*'}
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                <div className="space-y-1 text-center">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="file"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                    >
                      <span>აირჩიეთ ფაილი</span>
                      <input
                        id="file"
                        name="file"
                        type="file"
                        accept=".pdf"
                        onChange={handleFileChange}
                        className="sr-only"
                      />
                    </label>
                    <p className="pl-1">ან გადაიტანეთ აქ</p>
                  </div>
                  <p className="text-xs text-gray-500">PDF ფაილები, მაქსიმუმ 10MB</p>
                  {filePreview && (
                    <p className="text-sm text-green-600 font-medium">
                      არჩეული: {filePreview}
                    </p>
                  )}
                </div>
              </div>
            </div>


            {/* Submit Button */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/_panel-7c39f8/financial-documents')}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                გაუქმება
              </button>
              <button
                type="submit"
                disabled={saving || uploading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'შენახვა...' : uploading ? 'ფაილის ატვირთვა...' : 'შენახვა'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FinancialDocumentForm;
